import { z } from 'zod';
import { SETTINGS_NAV, SIDEBAR_NAV } from './nav';
import type { NavItem } from './nav';

/**
 * Release switches: which menu entries are open on this deployment, so modules can be released in
 * phases. This is 212Ez's own addition, not the reference's. It lives apart from the product code
 * (`shared/src/release.ts`, `server/src/release/`, `client/src/release/`); see docs/technical/release.md.
 *
 * A switch is keyed by the entry's page address. "/" is the dashboard (the home page).
 */
export const DASHBOARD_RELEASE_PATH = '/';

export interface ReleaseEntry {
  /** The entry's page address; also the switch's key. */
  path: string;
  /** The entry's permission code, shown next to its label. */
  code: string;
  label: string;
  /** The top-level menu group (or settings entry) it belongs to. */
  group: string;
  groupCode: string;
  menu: 'sidebar' | 'settings' | 'home';
}

function collect(items: NavItem[], menu: 'sidebar' | 'settings'): ReleaseEntry[] {
  const out: ReleaseEntry[] = [];
  const walk = (list: NavItem[], top: NavItem) => {
    for (const item of list) {
      if (item.path && item.code)
        out.push({
          path: item.path,
          code: item.code,
          label: item.label,
          group: top.label,
          groupCode: top.code ?? top.label,
          menu,
        });
      if (item.children) walk(item.children, top);
    }
  };
  for (const top of items) walk([top], top);
  return out;
}

/** Every switchable entry: the dashboard, the 85 sidebar entries and the 9 settings entries. */
export const RELEASE_ENTRIES: ReleaseEntry[] = [
  {
    path: DASHBOARD_RELEASE_PATH,
    code: 'Dashboard',
    label: 'Dashboard',
    group: 'Dashboard',
    groupCode: 'Dashboard',
    menu: 'home',
  },
  ...collect(SIDEBAR_NAV, 'sidebar'),
  ...collect(SETTINGS_NAV, 'settings'),
];

const RELEASE_PATHS = new Set(RELEASE_ENTRIES.map((e) => e.path));

/**
 * Feature switches: a feature added to a screen that may already be open. On a deployment with
 * phases set, each new feature stays off until it is ticked in the release console; with
 * everything released (a fresh install, development) every feature is on. The client hides an
 * off feature; the server refuses the API calls listed with it.
 */
export interface ReleaseFeature {
  key: string;
  label: string;
  /** The menu entry (page address) the feature is on; the console groups features by it. */
  entry: string;
  /** The day it was built, so the console shows what is new. */
  built: string;
  /** Part of launching: the console lists it first, under Launch, wherever `entry` points. */
  launch?: boolean;
  /** API calls only this feature makes (path under /api/v1 as a pattern), refused while it is off. */
  api: Array<{
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    path: string;
    /** Only when this query parameter is given. */
    query?: string;
    /** Only when the JSON body has these values (a new choice on an existing form). */
    body?: Record<string, string>;
  }>;
}

const ID = '[a-f0-9]{24}';
const customerApi = (rest: string) => `^/customers/${ID}/${rest}$`;
const billingApi = (rest: string) => `^/billing/customers/${ID}/${rest}$`;

export const RELEASE_FEATURES: ReleaseFeature[] = [
  {
    key: 'notes-mark-void',
    label: 'Mark Void (PC248) and View Archive Notes',
    entry: '/customers',
    built: '2026-10-20',
    api: [
      { method: 'POST', path: customerApi(`notes/${ID}/void`) },
      { method: 'GET', path: customerApi('notes'), query: 'archived' },
    ],
  },
  {
    key: 'notes-display',
    label: 'Display Notes (filters Notes, Posted By, Priority)',
    entry: '/customers',
    built: '2026-10-20',
    api: [{ method: 'GET', path: customerApi('notes/posted-by') }],
  },
  {
    key: 'verify-email',
    label: 'Verify Email on the customer page',
    entry: '/customers',
    built: '2026-10-20',
    api: [{ method: 'POST', path: customerApi('verify-email') }],
  },
  {
    key: 'add-line',
    label: 'Add Line (PC543)',
    entry: '/customers',
    built: '2026-10-20',
    api: [],
  },
  {
    key: 'change-account-type',
    label: 'Change Account Type (PC648): ACP / Lifeline',
    entry: '/customers',
    built: '2026-10-20',
    api: [
      { method: 'GET', path: customerApi('account-type') },
      { method: 'POST', path: customerApi('account-type') },
    ],
  },
  {
    key: 'add-product',
    label: 'Add Product (PC945)',
    entry: '/customers',
    built: '2026-10-20',
    api: [{ method: 'POST', path: customerApi('add-product') }],
  },
  {
    key: 'profile-rows',
    label:
      'Customer page rows: PIN, DID No, Company, Current Balance, Ported MDN, Plan Details, Query Usage, approval and plan expiry rows',
    entry: '/customers',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'profile-switches',
    label: 'Disable Credit Card (PC810), Do not sell info (PC846), Delete Data (PC847)',
    entry: '/customers',
    built: '2026-10-21',
    api: [{ method: 'PATCH', path: customerApi('flags') }],
  },
  {
    key: 'update-did-ivr-pin',
    label: 'Update DID and Update IVR PIN',
    entry: '/customers',
    built: '2026-10-21',
    api: [
      { method: 'PUT', path: customerApi('did') },
      { method: 'PUT', path: customerApi('ivr-pin') },
    ],
  },
  {
    key: 'highest-priority-notes',
    label: 'Highest Priority Notes',
    entry: '/customers',
    built: '2026-10-21',
    api: [{ method: 'PATCH', path: customerApi(`notes/${ID}/priority`) }],
  },
  {
    key: 'cancel-autopay',
    label: 'Cancel AutoPay on the customer page',
    entry: '/customers',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'current-shipping-info',
    label: 'Current Shipping Info',
    entry: '/customers',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'billing-autopay-log',
    label: 'Billing: Autopay Log (PC678)',
    entry: '/customers/find/billing',
    built: '2026-10-21',
    api: [{ method: 'GET', path: billingApi('autopay-log') }],
  },
  {
    key: 'billing-declined-payment',
    label: 'Billing: Declined Payment (PC932)',
    entry: '/customers/find/billing',
    built: '2026-10-21',
    api: [{ method: 'GET', path: billingApi('declined-payments') }],
  },
  {
    key: 'billing-ocs-purchase',
    label: 'Billing: OCS Purchase (PC465)',
    entry: '/customers/find/billing',
    built: '2026-10-21',
    api: [{ method: 'GET', path: billingApi('ocs-purchases') }],
  },
  {
    key: 'billing-apply-coupon',
    label: 'Billing: Apply Coupon on an invoice (PC1195)',
    entry: '/customers/find/billing',
    built: '2026-10-21',
    api: [{ method: 'POST', path: billingApi('apply-coupon') }],
  },
  {
    key: 'billing-tax-exemption',
    label: 'Billing: Tax Exemption Setting (PC459)',
    entry: '/customers/find/billing',
    built: '2026-10-21',
    api: [
      { method: 'GET', path: billingApi('tax-exemptions') },
      { method: 'PUT', path: billingApi('tax-exemptions') },
    ],
  },
  {
    key: 'group-replace-permissions',
    label: 'User groups: Replace Group Permissions',
    entry: '/settings/users',
    built: '2026-10-21',
    api: [{ method: 'POST', path: `^/user-groups/${ID}/replace-permissions$` }],
  },
  {
    key: 'modify-port-actions',
    label: 'Modify: Query portin, Update portin, Cancel portin of a port that is not finished',
    entry: '/customers',
    built: '2026-10-21',
    api: [
      { method: 'GET', path: `^/ports/in/by-customer/${ID}$` },
      { method: 'POST', path: `^/ports/in/${ID}/update$` },
    ],
  },
  {
    key: 'restore-payment',
    label: 'Update Account Status: Payment Information when restoring a line whose plan ran out',
    entry: '/customers',
    built: '2026-10-21',
    api: [
      { method: 'GET', path: customerApi('restore-payment/quote') },
      { method: 'POST', path: customerApi('restore-payment') },
    ],
  },
  {
    key: 'profile-suspended-date',
    label: 'Customer page: "Suspended" date row',
    entry: '/customers',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'portout-lock',
    label: 'Customer page: Portout Lock (and refusing port-outs of a locked number)',
    entry: '/customers',
    built: '2026-10-21',
    api: [{ method: 'PUT', path: customerApi('portout-lock') }],
  },
  {
    key: 'retention-note',
    label: 'Customer page: Retention Note',
    entry: '/customers',
    built: '2026-10-21',
    api: [{ method: 'POST', path: customerApi('retention-note') }],
  },
  {
    key: 'esim-qr-code',
    label: 'Customer page: Activation Code/QR-code of an eSIM line',
    entry: '/customers',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'balance-sms',
    label: 'Customer page: Send SMS (balance)',
    entry: '/customers',
    built: '2026-10-21',
    api: [{ method: 'POST', path: customerApi('balance-sms') }],
  },
  {
    key: 'note-ticket-link',
    label: 'Add New Note: "Do you want to create a ticket? (PC402)" link',
    entry: '/customers',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'header-inventory-search',
    label: 'Header: Inventory Search (the search box value looked up in inventory)',
    entry: '/inventory',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'header-payment-search',
    label: 'Header: Payment Search link',
    entry: '/customers/payment-search',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'reset-password',
    label: 'User menu: Reset Password (change your own password)',
    entry: '/',
    built: '2026-10-21',
    api: [{ method: 'POST', path: '^/auth/password$' }],
  },
  {
    key: 'review-nlad-subscriber-id',
    label: 'Fraud Orders Review Queue, Order Lookup: NLAD Subscriber ID filter',
    entry: '/review/fraud-orders',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'portin-address-fields',
    label:
      'Existing Subscriber Portin: Business name, New num local SP, Street number / type / direction',
    entry: '/ports/in/existing-subscriber',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'enrollment-port-in',
    label: 'Enrollment step 3: Port In handover (number brought from another provider)',
    entry: '/enrollments/new',
    built: '2026-10-21',
    api: [{ method: 'PUT', path: `^/enrollments/${ID}/step3$`, body: { handoverType: 'port-in' } }],
  },
  {
    key: 'enrollment-byod',
    label: 'Enrollment step 3: BYOD- Sprint/Verzion handover (PC250)',
    entry: '/enrollments/new',
    built: '2026-10-21',
    api: [{ method: 'PUT', path: `^/enrollments/${ID}/step3$`, body: { handoverType: 'byod' } }],
  },
  {
    key: 'enrollment-payment-step',
    label:
      'Enrollment step 4: the reference layout (tax per item, Receipt number, Coupon amount), Dealer wallet (PC544), Review and proceed',
    entry: '/enrollments/new',
    built: '2026-10-21',
    api: [
      { method: 'POST', path: `^/enrollments/${ID}/complete$`, body: { method: 'dealerWallet' } },
    ],
  },
  {
    key: 'report-payment-declined',
    label: 'All Reports: Payment Declined Report (PC932)',
    entry: '/reports',
    built: '2026-10-21',
    api: [{ method: 'GET', path: '^/reports/payment-declined(/export)?$' }],
  },
  {
    key: 'inventory-reports-2',
    label:
      'Reporting: SIM Inventory for Agents, GSM Device Inventory for Agents, Manage Unassigned Inventory, Manage Unassigned Device, SIM Summary, Unassigned Stock Report',
    entry: '/inventory/reporting',
    built: '2026-10-24',
    api: [
      {
        method: 'GET',
        path: '^/reports/(agent-sim-inventory|agent-device-inventory|unassigned-inventory|unassigned-device|sim-summary|unassigned-stock)(/export)?$',
      },
    ],
  },
  {
    key: 'inventory-tab-strip',
    label:
      'Manage Inventory: the tab strip Inventory Dashboard, Add Single Unit, Add Bulk Unit, Update Single Unit, Update Bulk Unit',
    entry: '/inventory',
    built: '2026-10-24',
    api: [],
  },
  {
    key: 'tickets-download-report',
    label: 'Tickets: Download Report (tickets counted per assignee and status)',
    entry: '/tickets',
    built: '2026-10-24',
    api: [{ method: 'GET', path: '^/tickets/report$' }],
  },
  {
    key: 'fraud-review-tabs',
    label:
      'Fraud Orders Review Queue: Live Orders, Website and Telesales Orders under Pending Orders',
    entry: '/review/fraud-orders',
    built: '2026-10-24',
    api: [{ method: 'GET', path: '^/reports/fraud-review-(live|website|telesales)(/export)?$' }],
  },
  {
    key: 'check-sim-compatibility',
    label:
      'Reporting: Check SIM Compatibility (asks the carrier whether a device works on its network)',
    entry: '/inventory/reporting',
    built: '2026-10-24',
    api: [{ method: 'POST', path: '^/esn-compatibility$' }],
  },
  {
    key: 'commission-redeem',
    label:
      'Commission matrix: View category, Set Redeem Rule (commission into the dealer wallet), Set Commission Limit Rule; Dealer Wallet: Redeem Commission',
    entry: '/settings/users',
    built: '2026-10-27',
    api: [
      { method: 'GET', path: '^/commission-matrices/(categories|redeem-rule|limits)$' },
      { method: 'PUT', path: '^/commission-matrices/(redeem-rule|limits)$' },
      { method: 'DELETE', path: `^/commission-matrices/limits/${ID}$` },
      { method: 'GET', path: '^/dealer-wallet/commission$' },
      { method: 'POST', path: '^/dealer-wallet/commission/redeem$' },
    ],
  },
  {
    key: 'monitoring-alerts',
    label:
      'System Health Status: Monitoring Panel Email Notification Setting (e-mail alerts per activity)',
    entry: '/',
    built: '2026-10-27',
    api: [
      { method: 'GET', path: '^/dashboard/monitoring/alert-settings$' },
      { method: 'PUT', path: '^/dashboard/monitoring/alert-settings$' },
    ],
  },
  {
    key: 'add-administrator',
    label:
      'User Settings: Manage administrator (add and edit company staff, who see the whole company)',
    entry: '/settings/users',
    built: '2026-10-27',
    api: [{ method: 'POST', path: '^/users$', body: { level: 'admin' } }],
  },
  {
    key: 'notification-spf-check',
    label: 'E-mail notifications: the SPF check of the From Email Address',
    entry: '/notifications/email/new',
    built: '2026-10-27',
    api: [{ method: 'POST', path: '^/notifications/spf-check$' }],
  },
  {
    key: 'reports-extras-2',
    label:
      "Reports: Enhance Payment's Exclude Wallet / Void Payments and four columns, Invoice Report's amount range, Wallet Balance's Discount Balance, the builders' Suspension Date",
    entry: '/reports',
    built: '2026-10-27',
    api: [],
  },
  {
    key: 'reporting-2',
    label:
      'Reporting: Agent Inventory Report, Agent Device Report, the Device Swap and Nlad Status Check links, and the stock counters',
    entry: '/inventory/reporting',
    built: '2026-10-27',
    api: [
      {
        method: 'GET',
        path: '^/reports/(agent-inventory-report|agent-device-report)(/export)?$',
      },
      { method: 'GET', path: '^/inventory-stock$' },
    ],
  },
  {
    key: 'report-generated-files',
    label:
      'All Reports: the files generated from 20 reports (Date Period, Generated Date, Download Link)',
    entry: '/reports',
    built: '2026-10-27',
    api: [{ method: 'GET', path: '^/reports/[a-z0-9-]+/files(/[a-f0-9]{24})?$' }],
  },
  {
    key: 'coupon-usage-report',
    label: 'Coupon codes: View Report (Coupon Code Usage Report)',
    entry: '/coupons',
    built: '2026-10-27',
    api: [{ method: 'GET', path: '^/reports/coupon-usage(/export)?$' }],
  },
  {
    key: 'permissions-all',
    label:
      "User groups: the reference's 449 other permission codes on the group page (not used by 212Ez yet)",
    entry: '/settings/users',
    built: '2026-10-27',
    api: [],
  },
  {
    key: 'customer-import',
    label:
      "Import customers: an MVNO's existing lines from its previous system's export (customer, SIM, enrollment), without asking the carrier",
    entry: '/settings/connections',
    built: '2026-10-30',
    launch: true,
    api: [{ method: 'POST', path: '^/bulk/import-customers(/file)?$' }],
  },
  {
    key: 'card-payments',
    label:
      'Credit Card payments through the payment gateway of Settings > Connections: enrollment, plan renew and change, restore, top-ups, pay invoices, add wallet; saved cards that can be charged; Auto Pay by card; card refunds',
    entry: '/customers',
    built: '2026-10-30',
    api: [
      { method: 'GET', path: '^/payments/config$' },
      { method: 'POST', path: '^/customers/[^/]+/cards/gateway$' },
    ],
  },
  {
    key: 'enrollment-device-id',
    label:
      "Enrollment step 3: Device ID (the customer's phone: IMEI, MEID or ESN), kept on the customer and sent to the carrier at activation",
    entry: '/enrollments/new',
    built: '2026-10-30',
    api: [],
  },
  {
    key: 'account-type-filters',
    label:
      "Account Type filter (Prepaid, Postpaid, Lifeline, Charity) on Customer Search, the enrollment lists and 14 reports (Activation, Revenue, Payment Detail, Enhance Payment, Payment Search, Customer List and more); and the customer's Charity switch",
    entry: '/customers',
    built: '2026-11-04',
    api: [
      { method: 'GET', path: '^/customers$', query: 'accountType' },
      { method: 'GET', path: '^/enrollments$', query: 'accountType' },
    ],
  },
  {
    key: 'account-type-filters-2',
    label:
      'Account Type filter (Prepaid, Postpaid, Lifeline, Charity) on 7 more reports: Email and SMS Notification, Autopay, Autopay Transaction, Prepaid Next Renew, Customer Disconnections with Reason, Customer Billing Code/PWG Cost Plan, Active Customer With Plan',
    entry: '/reports',
    built: '2026-11-05',
    api: [
      { method: 'GET', path: '^/reports/notification-report(/export)?$', query: 'accountType' },
      { method: 'GET', path: '^/reports/autopay(/export)?$', query: 'accountType' },
      { method: 'GET', path: '^/reports/autopay-transaction(/export)?$', query: 'accountType' },
      { method: 'GET', path: '^/reports/prepaid-next-renew(/export)?$', query: 'accountType' },
      { method: 'GET', path: '^/reports/customer-disconnections(/export)?$', query: 'accountType' },
      { method: 'GET', path: '^/reports/billing-code(/export)?$', query: 'accountType' },
      {
        method: 'GET',
        path: '^/reports/active-customer-with-plan(/export)?$',
        query: 'accountType',
      },
    ],
  },
  {
    key: 'lifeline-program-details',
    label:
      "Lifeline step 1, as on zipbellwireless.com: Public Housing Type under Federal Public Housing Assistance, and School Lunch/Breakfast with a required School that picks 'Child or Dependent in household'",
    entry: '/lifeline/new',
    built: '2026-11-04',
    api: [],
  },
  {
    key: 'lifeline-connection',
    label:
      'Settings > Connections: Lifeline (NLAD), the real NLAD connection: National Verifier check by Application ID, enroll after activation, update and de-enroll',
    entry: '/settings/connections',
    built: '2026-10-30',
    api: [
      { method: 'PUT', path: '^/connections/lifeline$' },
      { method: 'DELETE', path: '^/connections/lifeline$' },
      { method: 'POST', path: '^/connections/lifeline/test$' },
    ],
  },
  {
    key: 'plan-grace-actions',
    label:
      'Plan expiry: when the grace period of a plan ends, the line moves to the plan\'s "Status after grace" (Suspend or Deactivate) at the carrier',
    entry: '/settings/system',
    built: '2026-10-30',
    api: [],
  },
  {
    key: 'more-notification-events',
    label:
      'Notifications: also raise Order Confirmation, Activation confirmations, Payment failed, AutoPay CC Declined and Esim QR Code 4G',
    entry: '/notifications/email',
    built: '2026-10-30',
    api: [],
  },
  {
    key: 'payment-messages',
    label:
      'Notifications: also raise Payment confirmation (with %AMOUNT%) whenever money is taken and Refund (with %REFUNDED_AMOUNT%) when an invoice is refunded',
    entry: '/notifications/email',
    built: '2026-10-30',
    api: [],
  },
  {
    key: 'website',
    label:
      "The public website on this deployment's own host names (212ez.com, www). While off, those addresses go to the app's sign-in page",
    entry: '/settings/get-started',
    built: '2026-10-31',
    launch: true,
    api: [],
  },
  {
    key: 'website-logo',
    label:
      "The website's header and footer show the 212Ez logo (Option A, 2026-11-02) instead of the drawn name block. Needs the website switch too",
    entry: '/settings/get-started',
    built: '2026-11-02',
    launch: true,
    api: [],
  },
  {
    key: 'website-v2',
    label:
      "The new 212ez.com (owner's site of 2026-11-03, with its own pages for products, pricing, about and more) in place of the current one. Its Enroll and Login open the app's sign-up and sign-in. Needs the website switch too; its [placeholders] show until they are filled in",
    entry: '/settings/get-started',
    built: '2026-11-04',
    launch: true,
    api: [],
  },
  {
    key: 'platform-console',
    label:
      'Platform console (/platform-console): super admins, the list of companies, Move to Live, Suspend, temporary passwords, shared connectors and connector requests',
    entry: '/settings/get-started',
    built: '2026-10-31',
    launch: true,
    api: [
      { method: 'GET', path: '^/platform/' },
      { method: 'POST', path: '^/platform/' },
      { method: 'PUT', path: '^/platform/' },
    ],
  },
  {
    key: 'hq-instance',
    label:
      "The super instance hq.212ez.com: super admins sign in there and choose a company from the Company selector (or 212Ez's own sandbox company) to work in its full app. Needs the platform console and Open company switches too",
    entry: '/settings/get-started',
    built: '2026-11-04',
    launch: true,
    api: [
      { method: 'GET', path: '^/platform/hq/' },
      { method: 'POST', path: '^/platform/hq/' },
    ],
  },
  {
    key: 'zip-carrier-routing',
    label:
      'ZIP carrier routing: a plan with a wholesale code per carrier, the carriers that cover each ZIP (Service Area, coverage file import), the carrier picked by the ZIP in the order of SC73 (checked live with SC72 = Y), and the carrier choice at the SIM handover',
    entry: '/settings/service-area',
    built: '2026-11-05',
    api: [
      { method: 'GET', path: '^/service-areas/coverage$' },
      { method: 'POST', path: '^/service-areas/coverage$' },
      { method: 'GET', path: '^/service-areas$', query: 'carrierId' },
    ],
  },
  {
    key: 'hq-analytics',
    label:
      "HQ analytics: lines by Prepaid, Postpaid, Lifeline and Charity, activations, disconnects, ports, revenue, tax, refunds and estimated profit, for every company summed or one company, and each carrier's share and wholesale cost per line. Needs the super instance switch too",
    entry: '/settings/get-started',
    built: '2026-11-05',
    launch: true,
    api: [
      { method: 'GET', path: '^/platform/hq/analytics$' },
      { method: 'PUT', path: '^/platform/hq/carriers/[^/]+/cost$' },
    ],
  },
  {
    key: 'platform-support-session',
    label:
      "Platform console: Open company, a super admin enters a company's own app as 212Ez support (every screen, log, setting and record), audited under their login. Needs the platform console switch too",
    entry: '/settings/get-started',
    built: '2026-11-03',
    launch: true,
    api: [{ method: 'POST', path: '^/platform/companies/[^/]+/support-session$' }],
  },
  {
    key: 'platform-company-records',
    label:
      "Platform console: Details per company (its connections, never their secrets, and record counts) and Logs, every company's carrier and API calls and audit trail with filters. Needs the platform console switch too",
    entry: '/settings/get-started',
    built: '2026-11-03',
    launch: true,
    api: [
      { method: 'GET', path: '^/platform/companies/[^/]+/overview$' },
      { method: 'GET', path: '^/platform/logs$' },
    ],
  },
  {
    key: 'platform-new-company',
    label:
      'Platform console: New company, one-step onboarding: a super admin makes a company and its administrator in Trial. Needs the platform console switch too',
    entry: '/settings/get-started',
    built: '2026-10-31',
    launch: true,
    api: [{ method: 'POST', path: '^/platform/companies$' }],
  },
  {
    key: 'company-logo-url',
    label:
      'System Config: Company logo address, used as %COMPANY_LOGO_URL% in e-mail templates (the setting and its value in messages)',
    entry: '/settings/system',
    built: '2026-10-31',
    api: [],
  },
  {
    key: 'customer-portal',
    label:
      "Customer portal: a company's customers sign in on their own page (/portal/<company>) and renew, change plan, top up, add wallet money, pay invoices and manage Auto Pay. Its nine System Config settings",
    entry: '/customers',
    built: '2026-10-31',
    launch: true,
    api: [
      { method: 'GET', path: '^/portal/' },
      { method: 'POST', path: '^/portal/' },
      { method: 'PUT', path: '^/portal/' },
      { method: 'POST', path: '^/customers/[^/]+/portal-password$' },
    ],
  },
  {
    key: 'sample-setup',
    label:
      'Settings > Get started: Load a sample set-up, an invented carrier, plans, ZIP codes and SIMs, so a company in Trial can sell a line within minutes',
    entry: '/settings/get-started',
    built: '2026-10-31',
    launch: true,
    api: [{ method: 'POST', path: '^/onboarding/sample-setup$' }],
  },
  {
    key: 'sample-setup-remove',
    label:
      'Settings > Get started: Remove the sample set-up, so a company that tried the sample can go Live: its carrier, plans, top-up, SIM model, unsold SIMs and ZIP codes go, and any test sale stays as history',
    entry: '/settings/get-started',
    built: '2026-10-31',
    launch: true,
    api: [{ method: 'POST', path: '^/onboarding/sample-setup/remove$' }],
  },
  {
    key: 'setup-import',
    label:
      'Settings > Get started: import a set-up file (carriers, wholesale codes, plans, top-ups, service area, notification templates, settings) so a company moving to 212Ez only drops in its credentials',
    entry: '/settings/get-started',
    built: '2026-10-30',
    launch: true,
    api: [{ method: 'POST', path: '^/onboarding/setup-import$' }],
  },
  {
    key: 'mvno-signup',
    label:
      'MVNO sign-up: the public page /signup where an MVNO makes its own company and administrator (Trial: unconnected services simulated; the platform console moves it to Live)',
    entry: '/settings/connections',
    built: '2026-10-30',
    launch: true,
    api: [{ method: 'POST', path: '^/signup$' }],
  },
  {
    key: 'carrier-connector-builder',
    label:
      "Settings > Connections: carrier connectors made on the page, without code (a carrier's API address, sign-in, the values an MVNO enters and one request per action), with JSON import/export and Try a request",
    entry: '/settings/connections',
    built: '2026-10-30',
    api: [
      { method: 'GET', path: '^/connections/connectors$' },
      { method: 'POST', path: '^/connections/connectors$' },
      { method: 'PUT', path: `^/connections/connectors/${ID}$` },
      { method: 'DELETE', path: `^/connections/connectors/${ID}$` },
      { method: 'POST', path: '^/connections/carriers/[^/]+/try$' },
    ],
  },
  {
    key: 'no-simulation',
    label:
      'Live server: no simulated outside service. A service not connected in Settings > Connections is refused with a message instead of simulated (tick only once the services you use are connected)',
    entry: '/settings/connections',
    built: '2026-10-30',
    api: [],
  },
  {
    key: 'collection-status',
    label: 'Customer page: Collection Status (None, Full Payment, Partial Payment, ...)',
    entry: '/customers',
    built: '2026-10-29',
    api: [{ method: 'PUT', path: '^/customers/[^/]+/collection-status$' }],
  },
  {
    key: 'custom-builders-3',
    label:
      'Customize Reports: Subscriber & Inventory Weekly Report, Usage Details Report, High Usage Report',
    entry: '/reports/customize',
    built: '2026-10-29',
    api: [
      { method: 'GET', path: '^/reports/(weekly-summary|usage-details|high-usage)(/export)?$' },
      { method: 'GET', path: '^/custom-reports/run/(weekly-summary|usage-details|high-usage)$' },
      { method: 'POST', path: '^/custom-reports/saved$', body: { reportKey: 'weekly-summary' } },
      { method: 'POST', path: '^/custom-reports/saved$', body: { reportKey: 'usage-details' } },
      { method: 'POST', path: '^/custom-reports/saved$', body: { reportKey: 'high-usage' } },
    ],
  },
  {
    key: 'payment-refunds',
    label:
      'Billing and Invoice: Refund on a paid invoice; Reports: Payment Refunds Report; Customize Reports: Payment Refunds Report',
    entry: '/customers',
    built: '2026-10-29',
    api: [
      { method: 'POST', path: '^/billing/customers/[^/]+/invoices/[^/]+/refund$' },
      { method: 'GET', path: '^/reports/payment-refunds(/export)?$' },
      { method: 'GET', path: '^/custom-reports/run/payment-refunds$' },
      { method: 'POST', path: '^/custom-reports/saved$', body: { reportKey: 'payment-refunds' } },
    ],
  },
  {
    key: 'commission-add-category',
    label:
      'Manage Commission Matrix: Add Category, and the flat amount an added category pays on its event',
    entry: '/settings/users',
    built: '2026-10-29',
    api: [{ method: 'POST', path: '^/commission-matrices/categories$' }],
  },
  {
    key: 'reporting-3',
    label:
      'Reporting: SIM Batch Report, Device Report, Universal Inventory Reports, Vendor Summary, the two Creating Boxes reports, SIM/UICCID Swap Report, MDN Plan Change Report',
    entry: '/inventory/reporting',
    built: '2026-10-29',
    api: [
      {
        method: 'GET',
        path: '^/reports/(sim-batch|device-batch|universal-inventory|vendor-summary|sim-boxes|device-boxes|sim-swap)(/export)?$',
      },
    ],
  },
  {
    key: 'brand-look',
    label:
      "212Ez branding: the name block 'MVNO Cloud Solutions, Powered By 212Ez, Telecom Made Easy' and the navy and amber colours, on every screen",
    entry: '/',
    built: '2026-10-28',
    api: [],
  },
  {
    key: 'brand-lockup-image',
    label:
      "212Ez logo image (2026-11-02): 'MVNO Cloud Solutions, Powered By 212Ez, Telecom Made Easy' as the owner's own artwork on the sign-in, password and sign-up pages and in the header, in place of the drawn name block. Needs 212Ez branding on",
    entry: '/',
    built: '2026-11-02',
    api: [],
  },
  {
    key: 'brand-logo',
    label: "212Ez's logo in the header, on the sign-in page and on the password page",
    entry: '/',
    built: '2026-10-27',
    api: [],
  },
  {
    key: 'ai-assistant',
    label:
      'Header: AI Assistant (a read-only lookup of customers, SIMs, invoices, counts and reports; no outside AI)',
    entry: '/',
    built: '2026-10-24',
    api: [{ method: 'POST', path: '^/assistant/ask$' }],
  },
  {
    key: 'support-page',
    label: "Header: Support (212Ez's own contact note and FAQ)",
    entry: '/',
    built: '2026-10-24',
    api: [],
  },
  {
    key: 'system-health',
    label:
      'Header: System Health Status, the Monitoring Panel (Activity Success Rate, Scheduled Autopay, Scheduled Renewals; refreshed every 5 minutes)',
    entry: '/',
    built: '2026-10-24',
    api: [{ method: 'GET', path: '^/dashboard/monitoring$' }],
  },
  {
    key: 'customer-account-dialogs',
    label:
      'Customer page: Update plan custom expiry date, Update Customer Classification (and its list), Collection Protection Status, Suppress Bills, the winback notification',
    entry: '/customers',
    built: '2026-10-24',
    api: [
      { method: 'PUT', path: customerApi('plan-custom-expiry') },
      { method: 'PUT', path: customerApi('classification') },
      { method: 'PUT', path: customerApi('collection-protection') },
      { method: 'PUT', path: customerApi('suppress-bills') },
      { method: 'POST', path: customerApi('winback') },
      { method: 'GET', path: '^/settings-lists/customer-classifications' },
      { method: 'POST', path: '^/settings-lists/customer-classifications' },
      { method: 'PUT', path: '^/settings-lists/customer-classifications' },
    ],
  },
  {
    key: 'used-system-configurations',
    label:
      'Every page that reads System Config settings: "Used system configurations", the settings it uses and their values',
    entry: '/settings/system',
    built: '2026-10-23',
    api: [{ method: 'GET', path: '^/system-configs/used$' }],
  },
  {
    key: 'forgot-password',
    label:
      'Sign in: "Forgot your password?" (an e-mailed link to choose a new password, valid 30 minutes)',
    entry: '/settings/users',
    built: '2026-10-23',
    api: [
      { method: 'POST', path: '^/auth/forgot-password$' },
      { method: 'POST', path: '^/auth/reset-password$' },
    ],
  },
  {
    key: 'shipper-inventory',
    label:
      'Dropship Orders: Manage Shipper Inventory (Single Unit and Bulk Upload) and the Shipper Batch Report',
    entry: '/inventory/dropship-orders',
    built: '2026-10-23',
    api: [
      { method: 'POST', path: '^/shipments/shipper-unit$' },
      { method: 'POST', path: '^/bulk/shipper-inventory(/file)?$' },
    ],
  },
  {
    key: 'portin-who-got-it-back',
    label:
      'Existing Subscriber Portin: Who got it back(Employee LoginID) for the old SIM, IMEI and Plan name',
    entry: '/ports/in/existing-subscriber',
    built: '2026-10-23',
    api: [],
  },
  {
    key: 'inventory-report-links',
    label: "Reporting (PC19): the reference's report links, for the reports 212Ez has",
    entry: '/inventory/reporting',
    built: '2026-10-23',
    api: [],
  },
  {
    key: 'enrollment-row-options',
    label:
      'Enrollment lists: "USPS address request response" and, on Incomplete enrollments, "Send Enrollment Reminder"',
    entry: '/enrollments/incomplete',
    built: '2026-10-23',
    api: [
      { method: 'GET', path: `^/enrollments/${ID}/address-checks$` },
      { method: 'POST', path: `^/enrollments/${ID}/reminder$` },
    ],
  },
  {
    key: 'shipping-queue-order-details',
    label:
      'Shipping Queue: View Order Details (Attach SIM / DEVICE, Shipper, Submit Bulk Shipment, Print) and USPS Address Request and Response',
    entry: '/inventory/shipping-queue',
    built: '2026-10-23',
    api: [
      { method: 'GET', path: `^/shipments/${ID}/details$` },
      { method: 'POST', path: `^/shipments/${ID}/ship$` },
      { method: 'GET', path: `^/shipments/${ID}/address-checks$` },
    ],
  },
  {
    key: 'dealer-wallet-share',
    label:
      "Dealer Wallet: Create your wallet, Share Wallet (a shared wallet pays the user's dealer-wallet orders), Other User ID on Transfer Funds, the Activity Log tab and Remove Funds by wallet",
    entry: '/dealer-wallet',
    built: '2026-10-23',
    api: [
      { method: 'POST', path: '^/dealer-wallet/create$' },
      { method: 'POST', path: '^/dealer-wallet/share$' },
      { method: 'GET', path: '^/dealer-wallet/activity$' },
      { method: 'GET', path: '^/dealer-wallet/wallets$' },
    ],
  },
  {
    key: 'custom-subscriber-history',
    label:
      'Customize Reports: Subscriber History (A/C status, address, CSR search, SIM, MDN, name, plan and resend history)',
    entry: '/reports/customize',
    built: '2026-10-23',
    api: [
      { method: 'GET', path: '^/reports/subscriber-history(/export)?$' },
      { method: 'GET', path: '^/custom-reports/run/subscriber-history$' },
      {
        method: 'POST',
        path: '^/custom-reports/saved$',
        body: { reportKey: 'subscriber-history' },
      },
    ],
  },
  {
    key: 'custom-builders-2',
    label:
      'Customize Reports: Plan Changes Log Report, Simple Commission Report, Inventory, Tax report',
    entry: '/reports/customize',
    built: '2026-10-24',
    api: [
      {
        method: 'GET',
        path: '^/reports/(plan-changes-log|simple-commission|inventory-units|tax-report)(/export)?$',
      },
      {
        method: 'GET',
        path: '^/custom-reports/run/(plan-changes-log|simple-commission|inventory|tax-report)$',
      },
      {
        method: 'POST',
        path: '^/custom-reports/saved$',
        body: { reportKey: 'plan-changes-log' },
      },
      { method: 'POST', path: '^/custom-reports/saved$', body: { reportKey: 'simple-commission' } },
      { method: 'POST', path: '^/custom-reports/saved$', body: { reportKey: 'inventory' } },
      { method: 'POST', path: '^/custom-reports/saved$', body: { reportKey: 'tax-report' } },
    ],
  },
  {
    key: 'custom-product-purchase-log',
    label: 'Customize Reports: Product Purchase Log Report',
    entry: '/reports/customize',
    built: '2026-10-21',
    api: [
      { method: 'GET', path: '^/reports/product-purchase-log(/export)?$' },
      { method: 'GET', path: '^/custom-reports/run/product-purchase-log$' },
      {
        method: 'POST',
        path: '^/custom-reports/saved$',
        body: { reportKey: 'product-purchase-log' },
      },
    ],
  },
  {
    key: 'catalogue-images',
    label: 'Manage Model: Image 1 to 3; Manage Equipment: Image 1',
    entry: '/inventory/models',
    built: '2026-10-21',
    api: [
      { method: 'POST', path: `^/product-models/${ID}/images/[1-3]$` },
      { method: 'DELETE', path: `^/product-models/${ID}/images/[1-3]$` },
      { method: 'POST', path: `^/equipment/${ID}/image$` },
      { method: 'DELETE', path: `^/equipment/${ID}/image$` },
    ],
  },
  {
    key: 'lifeline-remove',
    label:
      'Change Account Type: Remove Lifeline (the line moves to the cheapest prepaid plan of its carrier and state)',
    entry: '/customers',
    built: '2026-10-28',
    api: [{ method: 'POST', path: '^/lifeline/customers/[^/]+/remove$' }],
  },
  {
    key: 'inventory-report-subreports',
    label:
      "Inventory Report's ten reports: Agent Wise, By Agent, Detail, Snapshot and Transaction reports for SIMs and devices",
    entry: '/inventory/report',
    built: '2026-10-27',
    api: [],
  },
  {
    key: 'reports-extras-3',
    label:
      "Reports: Coupon Code Usage Report's Category, Enhance Payment's Equipment Discount, Tax Breakup, Retailer ID and Last Purchase Date, Prepaid Next Renew's Prepaid Plan Renew on",
    entry: '/reports',
    built: '2026-10-27',
    api: [],
  },
  {
    key: 'model-details-2',
    label:
      'Manage Model: Phone Capable, Handover, E-SIM Compatible, Client Type, Position, Activation Fees, Co-pay Amount, Short Description and Note / Description',
    entry: '/inventory/models',
    built: '2026-10-27',
    api: [],
  },
  {
    key: 'report-filters-3',
    label:
      "All Reports: User Login Log Report's Group Type and Customer Note's Note Status filters",
    entry: '/reports',
    built: '2026-10-27',
    api: [],
  },
  {
    key: 'inventory-full-dashboard',
    label:
      'Inventory Dashboard: View Full Dashboard (Full Inventory Dashboard with SIM & Device Add/Update Activity and Quick Reports) and the All Products filter',
    entry: '/inventory',
    built: '2026-10-27',
    api: [{ method: 'GET', path: '^/inventory/activity$' }],
  },
  {
    key: 'inventory-tile-links',
    label:
      'Inventory Dashboard: a KPI tile opens its tab (IMEI tiles the Device / IMEI tab, SIM tiles the SIM tab)',
    entry: '/inventory',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'shipper-package-settings',
    label:
      'Shipper Settings: Endicia package settings (Weight Oz, Mail Class, Mailpiece Shape, Dimensions, Cost Center, Tax Identification No)',
    entry: '/settings/shipper',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'group-export',
    label: 'User Settings > Manage user groups: Export',
    entry: '/settings/users',
    built: '2026-10-21',
    api: [{ method: 'GET', path: '^/user-groups/export$' }],
  },
  {
    key: 'api-log-reset',
    label: 'REST API Log: Reset Search',
    entry: '/logs/rest-api',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'portin-subscriber-search',
    label: 'Existing Subscriber Portin: Search by customer name, enrollment id, Customer id',
    entry: '/ports/in/existing-subscriber',
    built: '2026-10-21',
    api: [{ method: 'GET', path: '^/ports/subscriber-search$' }],
  },
  {
    key: 'enrollment-phone-cost',
    label: 'Completed enrollments: Phone cost column',
    entry: '/enrollments/completed',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'who-got-it-back',
    label:
      'ESN/SIM Change, Change Carrier, Change Device ID: Who got it back (Employee Login ID), with the old SIM or device put in that user stock',
    entry: '/customers',
    built: '2026-10-21',
    api: [{ method: 'GET', path: '^/users/suggest$' }],
  },
  {
    key: 'purchase-reload',
    label: 'Purchase History: Reload Purchases',
    entry: '/customers',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'resend-30-days',
    label: 'Resend Notification: the most recent 30 days only, with the reference note',
    entry: '/customers',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'carrier-code-plans',
    label: 'Plan Config > Carrier Details: the plans of each carrier code as child rows',
    entry: '/settings/plans',
    built: '2026-10-21',
    api: [{ method: 'GET', path: `^/carrier-codes/${ID}/plans$` }],
  },
  {
    key: 'dropship-order-types',
    label: 'Dropship Orders: Pending Orders by order type, and View Shipment Order Details',
    entry: '/inventory/dropship-orders',
    built: '2026-10-21',
    api: [
      { method: 'GET', path: '^/shipments/pending-by-type$' },
      { method: 'GET', path: '^/shipments/pending-orders$' },
    ],
  },
  {
    key: 'password-expiry',
    label:
      'System Config: Login Password Expiry Days (SC99), and choosing a new password at sign-in',
    entry: '/settings/system',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'login-limits',
    label:
      'System Config: Prevent User Multiple Login (SC227), Allowed Multiple Login (SC228), agent password expiry when not signed in (SC1490)',
    entry: '/settings/system',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'campaign-worker',
    label: 'Campaigns: a batch scheduled for a later day is sent on that day by the server',
    entry: '/campaigns',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'notification-events',
    label:
      'Notifications: also raise Autopay Set Up / Cancelled / Processed / Failure, Renew Plan, Successful Payment: Plan, Cancel order, Change Email, IVR pin generate, Port Out Request / Success / Fail, Ticket Notification, Data top up added',
    entry: '/notifications/email',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'bad-reasons',
    label:
      'ESN/SIM Change and Change Device ID: Reason for Bad ESN/SIM, Reason For Bad Device (asked when the reason marks the old card Bad)',
    entry: '/customers',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'system-config-all',
    label:
      'System Config: every setting of the reference (331 more, marked "not used yet" until a feature reads them, and 12 that act: SC103, SC140, SC397, SC1648, SC82-SC85, SC370, SC371, SC259, SC260), its order and collapsed groups, Search System Config',
    entry: '/settings/system',
    built: '2026-10-23',
    api: [],
  },
  {
    key: 'group-limits',
    label: 'User groups: discount credit and wallet limits (and their enforcement)',
    entry: '/settings/users',
    built: '2026-10-21',
    api: [],
  },
  {
    key: 'group-assign',
    label:
      'User groups: Assign Group (the groups a group may give to the users its members add or edit)',
    entry: '/settings/users',
    built: '2026-10-24',
    api: [{ method: 'PUT', path: `^/user-groups/${ID}/assignable$` }],
  },
];

const FEATURE_KEYS = new Set(RELEASE_FEATURES.map((f) => f.key));
const FEATURE_PATTERNS = RELEASE_FEATURES.flatMap((f) =>
  f.api.map((a) => ({
    feature: f,
    method: a.method,
    path: new RegExp(a.path),
    query: a.query,
    body: a.body,
  })),
);

/**
 * Every feature an API call belongs to (path under /api/v1). A call can need more than one: New
 * company is a feature inside the platform console, and both must be on.
 */
export function featuresForRequest(
  method: string,
  path: string,
  query: Record<string, unknown> = {},
  body: unknown = undefined,
): ReleaseFeature[] {
  const fields = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  return FEATURE_PATTERNS.filter(
    (p) =>
      p.method === method.toUpperCase() &&
      p.path.test(path) &&
      (!p.query ||
        (query[p.query] !== undefined && query[p.query] !== '' && query[p.query] !== 'false')) &&
      (!p.body || Object.entries(p.body).every(([k, v]) => fields[k] === v)),
  ).map((p) => p.feature);
}

/** The first feature an API call belongs to, if any. */
export const featureForRequest = (
  ...args: Parameters<typeof featuresForRequest>
): ReleaseFeature | undefined => featuresForRequest(...args)[0];

/** Whether a feature is on. With everything released every feature is on. */
export function isFeatureReleased(
  state: Pick<ReleaseStateDto, 'allReleased' | 'features'>,
  key: string,
): boolean {
  return state.allReleased || (state.features ?? []).includes(key);
}

/** Phase 1, as decided by the owner on 2026-10-16. */
export const PHASE_1_RELEASE: string[] = [
  '/settings/plans',
  '/settings/users',
  '/tickets',
  '/customers',
];

export const releaseUpdateSchema = z.object({
  /** true = every entry is open, whatever `released` holds (the state before any phase is set). */
  allReleased: z.boolean(),
  released: z
    .array(z.string())
    .max(RELEASE_ENTRIES.length)
    .superRefine((paths, ctx) =>
      paths.forEach((path, i) => {
        if (!RELEASE_PATHS.has(path))
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i], message: 'Unknown menu entry' });
      }),
    ),
  /** The features switched on (keys of RELEASE_FEATURES). */
  features: z
    .array(z.string())
    .max(RELEASE_FEATURES.length)
    .default([])
    .superRefine((keys, ctx) =>
      keys.forEach((key, i) => {
        if (!FEATURE_KEYS.has(key))
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i], message: 'Unknown feature' });
      }),
    ),
});
export type ReleaseUpdateInput = z.input<typeof releaseUpdateSchema>;

export interface ReleaseStateDto {
  allReleased: boolean;
  released: string[];
  features: string[];
  updatedAt: string | null;
}

export interface ReleaseChangeDto {
  id: string;
  allReleased: boolean;
  released: string[];
  features: string[];
  at: string;
}

/**
 * The entry a page address belongs to: the longest entry address that starts it, so a customer's
 * own pages (/customers/123/topup) follow Customer Profile. Addresses outside the menu return
 * undefined and are never switched.
 */
export function releasePathFor(pathname: string): string | undefined {
  if (pathname === DASHBOARD_RELEASE_PATH) return DASHBOARD_RELEASE_PATH;
  // The wizard's pages belong to "New enrollments", not to the list that shares their prefix.
  if (/^\/enrollments\/[^/]+\/(step|done)/.test(pathname)) return '/enrollments/new';
  let best: string | undefined;
  for (const { path } of RELEASE_ENTRIES) {
    if (path === DASHBOARD_RELEASE_PATH) continue;
    const match = pathname === path || pathname.startsWith(`${path}/`);
    if (match && (!best || path.length > best.length)) best = path;
  }
  return best;
}

/** Whether an entry (by its address) is open. Addresses outside the menu are always open. */
export function isEntryReleased(
  state: Pick<ReleaseStateDto, 'allReleased' | 'released'>,
  entryPath: string | undefined,
): boolean {
  if (state.allReleased || !entryPath) return true;
  return state.released.includes(entryPath);
}
